import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const F = [
  { name: "company_name", label: "Name of the Firm", ph: "Acme Ceramics Pvt Ltd", required: true },
  { name: "mobile", label: "Mobile Number", ph: "+91 98xxxx xxxxx", required: true },
  { name: "address", label: "Address", ph: "Street, City" },
  { name: "location", label: "Location", ph: "City, State" },
  { name: "gst_no", label: "GST No.", ph: "27ABCDE1234F1Z5", required: true },
];
const SALUTATIONS = ["Mr", "Mrs", "Miss"];

export default function BuyerSignup() {
  const { signupBuyer } = useApp();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromUrl = (searchParams.get("ref") || "").trim().toUpperCase();
  const [form, setForm] = useState({
    salutation: "Mr", company_name: "", person_name: "", mobile: "",
    address: "", location: "", gst_no: "",
    password: "", confirm_password: "",
    employee_referral_code: refFromUrl,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (refFromUrl && refFromUrl !== form.employee_referral_code) {
      setForm(f => ({ ...f, employee_referral_code: refFromUrl }));
    }
    // eslint-disable-next-line
  }, [refFromUrl]);

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["company_name","person_name","mobile","address","location","gst_no","password"]) {
      if (!form[k]) { toast.error(`Please fill ${k.replace(/_/g," ")}`); return; }
    }
    if (form.password !== form.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await signupBuyer({
        company_name: form.company_name,
        person_name: form.person_name,
        mobile: form.mobile,
        address: form.address,
        location: form.location,
        gst_no: form.gst_no,
        password: form.password,
        employee_referral_code: form.employee_referral_code || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="signup-pending">
        <div className="w-14 h-14 bg-[#C9A968] rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-[#1E2C5A]" strokeWidth={2} />
        </div>
        <p className="label-eyebrow mt-8">Awaiting Approval</p>
        <h1 className="font-editorial text-5xl tracking-editorial mt-4 mb-6">Application received</h1>
        <p className="text-[#5C5C5C] leading-relaxed">
          Your account is under review. Our team will approve it within <strong>24 hours</strong>.
          You'll receive a WhatsApp notification once approved and can then sign in with your mobile and password.
        </p>
        <button className="btn-outline-ink mt-10" onClick={() => nav("/")} data-testid="signup-done-home">Return home</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="buyer-signup-page">
      <p className="label-eyebrow">Trade Registration</p>
      <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3 mb-2">
        Lets Join Hands
      </h1>
      <p className="text-[#5C5C5C] mb-12 max-w-xl">
        A curated portal for businesses. Complete the form — a TileRoom representative will verify and approve your account within 24 hours.
      </p>

      {refFromUrl && (
        <div className="mb-8 flex items-center gap-3 border border-[#C9A968] bg-[#C9A968]/10 px-4 py-3 max-w-xl" data-testid="signup-ref-banner">
          <Sparkles className="w-4 h-4 text-[#C9A968] flex-shrink-0" strokeWidth={1.5} />
          <p className="text-sm text-[#1E2C5A]">
            You were referred by TileRoom employee <span className="font-editorial text-base">{refFromUrl}</span>. They'll be credited when your order is placed.
          </p>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="signup-form">
        <div className="md:col-span-2 grid grid-cols-[110px,1fr] gap-4">
          <div>
            <label className="label-eyebrow block mb-1">Prefix</label>
            <select
              className="w-full bg-transparent border-0 border-b border-[#d6d3ce] py-3 text-base outline-none focus:border-[#1E2C5A]"
              value={form.salutation}
              onChange={(e) => setForm({ ...form, salutation: e.target.value })}
              data-testid="signup-salutation"
            >
              {SALUTATIONS.map(s => <option key={s} value={s}>{s}.</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Name of the Person</label>
            <input
              className="input-editorial"
              value={form.person_name}
              onChange={(e) => setForm({ ...form, person_name: e.target.value })}
              placeholder="Full name"
              data-testid="signup-person_name"
            />
          </div>
        </div>
        {F.map(f => (
          <div key={f.name}>
            <label className="label-eyebrow block mb-1">
              {f.label}{f.required && <span className="text-red-700 ml-1">*</span>}
            </label>
            <input
              className="input-editorial"
              value={form[f.name]}
              onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
              placeholder={f.ph}
              data-testid={`signup-${f.name}`}
            />
          </div>
        ))}
        <div>
          <label className="label-eyebrow block mb-1">Password<span className="text-red-700 ml-1">*</span></label>
          <input type="password" className="input-editorial" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" data-testid="signup-password" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Confirm Password<span className="text-red-700 ml-1">*</span></label>
          <input type="password" className="input-editorial" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} placeholder="Re-enter password" data-testid="signup-confirm" />
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-1">Employee Referral Code <span className="text-[#8a8a8a]">(optional)</span></label>
          <input
            className="input-editorial"
            value={form.employee_referral_code}
            onChange={(e) => setForm({ ...form, employee_referral_code: e.target.value.toUpperCase() })}
            placeholder="TR0001-XXXX (if a TileRoom employee referred you)"
            data-testid="signup-employee_referral_code"
          />
        </div>
        <div className="md:col-span-2 flex items-center justify-between mt-4">
          <Link to="/login" className="link-slide text-sm text-[#1E2C5A]" data-testid="link-back-login">← Back to sign in</Link>
          <button type="submit" disabled={busy} className="btn-clay" data-testid="signup-submit">
            {busy ? "Submitting..." : "Submit for Approval"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </form>
    </div>
  );
}
